import { useQueryClient } from '@tanstack/react-query'
import {
  usePutEnginesId,
  getGetEnginesIdQueryKey,
  type PutEnginesIdMutationBody,
} from '@/types/origin-point-engine'
import { showErrorData, showSuccessData } from '@/lib/toast'
import { useEditorStore } from '@/features/editor/stores/editor-store'
import type {
  EditorDraft,
  EditorKnowledge,
} from '@/features/editor/types/editor.types'
import type {
  KnowledgeEntry,
  KeywordTriggerOp,
} from '@/features/editor/types/knowledge.types'
import {
  getExclusiveTags,
  normalizeTags,
} from '@/features/editor/lib/editor-tags'
import {
  normalizeUserCardEntries,
} from '@/features/editor/lib/editor-user-card'
import { validateEditorSave } from '@/features/editor/lib/editor-validate'

type SaveEntryPayload =
  | {
      id: string
      title: string
      content: string
      enabled: boolean
      retrievalMethod: 'permanent'
      keywordLogic?: KeywordTriggerOp
      isUserCard?: boolean
    }
  | {
      id: string
      title: string
      content: string
      enabled: boolean
      retrievalMethod: 'prompt'
      keywords: string[]
      keywordLogic: KeywordTriggerOp
      isUserCard?: boolean
    }

function normalizeKeywords(v: unknown): string[] {
  return Array.isArray(v)
    ? v.filter((x) => typeof x === 'string' && x.trim().length > 0)
    : []
}

function normalizeKeywordLogic(v: unknown): KeywordTriggerOp {
  return v === 'and' ? 'and' : 'or'
}

function mapEntryToPayload(
  e: KnowledgeEntry,
  opts?: { includeUserCard?: boolean }
): SaveEntryPayload {
  if (e.retrievalMethod === 'prompt') {
    return {
      id: e.id,
      title: e.title,
      content: e.content,
      enabled: e.enabled,
      retrievalMethod: 'prompt',
      keywords: normalizeKeywords(e.keywords),
      keywordLogic: normalizeKeywordLogic(e.keywordOp),
      ...(opts?.includeUserCard ? { isUserCard: e.isUserCard } : {}),
      // keywordOp 不发（后端未支持）
    }
  }

  return {
    id: e.id,
    title: e.title,
    content: e.content,
    enabled: e.enabled,
    retrievalMethod: 'permanent',
    ...(opts?.includeUserCard ? { isUserCard: e.isUserCard } : {}),
    // keywords 不发
    // keywordOp 不发
  }
}

/**
 * Editor state -> 后端更新引擎请求体（只包含你需要保存的字段）
 * 不发送 keywordOp 等后端未接入字段
 */
function buildUpdateRequest(
  draft: EditorDraft,
  knowledge: EditorKnowledge,
  opts: {
    tags: string[]
    allowCustomUser: boolean
    characterProfiles: KnowledgeEntry[]
  }
): PutEnginesIdMutationBody {
  // 这里保持你之前的“以真实返回体为准”的策略：
  // 如果后端类型和前端字段不完全对齐，允许用断言兜底
  return {
    name: draft.title,
    description: draft.description,
    tags: opts.tags,
    allowCustomUser: opts.allowCustomUser,

    // 后端字段：imageUrl / backgroundImageUrl
    imageUrl: draft.coverImageUrl,
    backgroundImageUrl: draft.backgroundImageUrl,

    settings: {
      narrativeCore: draft.narrativeCore,
      firstMessage: draft.firstMessage,
    },

    worldInfo: (knowledge.worldInfo ?? []).map((entry) =>
      mapEntryToPayload(entry)
    ),
    characterProfiles: (opts.characterProfiles ?? []).map((entry) =>
      mapEntryToPayload(entry, { includeUserCard: true })
    ),
  } as unknown as PutEnginesIdMutationBody
}

export function useSaveDraft() {
  const queryClient = useQueryClient()

  const engineId = useEditorStore((s) => s.draft.engineId)
  const setSaving = useEditorStore((s) => s.setSaving)

  const mutation = usePutEnginesId({
    mutation: {
      onMutate: () => {
        setSaving(true)
      },

      onSuccess: async () => {
        // ✅ 保存成功：只更新草稿基线（不影响线上基线）
        useEditorStore.getState().markSaved()

        // ✅ 发布态保存不自动上线：toast 明确表达
        const st = useEditorStore.getState()
        const isPublished = st.publishStatus === 'published'
        showSuccessData(isPublished ? '草稿已保存（未自动上线）' : '草稿已保存')

        if (engineId) {
          await queryClient.invalidateQueries({
            queryKey: getGetEnginesIdQueryKey(engineId),
          })
        }
      },

      onError: () => {
        showErrorData('保存失败')
      },

      onSettled: () => {
        setSaving(false)
      },
    },
  })

  function saveDraft() {
    if (!engineId) {
      showErrorData('无法保存', '缺少 engineId')
      return
    }

    // ✅ 触发保存时，从 store 取最新快照（避免闭包旧值）
    const st = useEditorStore.getState()
    const validationError = validateEditorSave(st.draft, st.knowledge)
    if (validationError) {
      showErrorData('无法保存', validationError)
      return
    }
    const normalizedTags = normalizeTags(st.draft.tags)
    const identity = getExclusiveTags(normalizedTags, 'identity')
    const allowCustomUser = identity[0] === '自定主角'

    const normalizedProfiles = normalizeUserCardEntries(
      st.knowledge.characterProfiles ?? [],
      allowCustomUser
    )

    if (normalizedProfiles.adjusted) {
      const store = useEditorStore.getState()
      for (const entry of normalizedProfiles.entries) {
        store.patchEntry('characterProfiles', entry.id, {
          isUserCard: entry.isUserCard,
        })
      }
    }

    const data = buildUpdateRequest(st.draft, st.knowledge, {
      tags: normalizedTags,
      allowCustomUser,
      characterProfiles: normalizedProfiles.entries,
    })

    mutation.mutate({ id: engineId, data })
  }

  async function saveDraftAsync() {
    if (!engineId) {
      showErrorData('无法保存', '缺少 engineId')
      throw new Error('Missing engineId')
    }

    const st = useEditorStore.getState()
    const validationError = validateEditorSave(st.draft, st.knowledge)
    if (validationError) {
      showErrorData('无法保存', validationError)
      throw new Error(validationError)
    }
    const normalizedTags = normalizeTags(st.draft.tags)
    const identity = getExclusiveTags(normalizedTags, 'identity')
    const allowCustomUser = identity[0] === '自定主角'

    const normalizedProfiles = normalizeUserCardEntries(
      st.knowledge.characterProfiles ?? [],
      allowCustomUser
    )

    if (normalizedProfiles.adjusted) {
      const store = useEditorStore.getState()
      for (const entry of normalizedProfiles.entries) {
        store.patchEntry('characterProfiles', entry.id, {
          isUserCard: entry.isUserCard,
        })
      }
    }

    const data = buildUpdateRequest(st.draft, st.knowledge, {
      tags: normalizedTags,
      allowCustomUser,
      characterProfiles: normalizedProfiles.entries,
    })

    return mutation.mutateAsync({ id: engineId, data })
  }

  return {
    saveDraft,
    saveDraftAsync,
    isSaving: mutation.isPending,
    error: mutation.error,
  }
}
